import { LegalInfo } from "../components/LegalInfo";
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Nav } from "../components/Nav";
import { useAuth } from "../AuthContext";
import { useLang } from "../i18n";
import { apiError } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PasswordInput } from "../components/PasswordInput";
import { FooterLinks } from "../components/FooterLinks";
import { ConsentBoxes } from "../components/ConsentBoxes";
import { RiseLogo } from "../components/RiseLogo";
import { toast } from "sonner";

export default function Auth({ mode, tenant = null }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", first_name: "", last_name: "", invite_code: "" });
  const [err, setErr] = useState("");
  const [consents, setConsents] = useState({ terms_privacy: false, health_data: false });
  const [consentErr, setConsentErr] = useState("");
  const [busy, setBusy] = useState(false);
  const accent = (tenant && /^#[0-9a-fA-F]{6}$/.test(tenant.accent_color || "")) ? tenant.accent_color : "#3F6E85";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setConsentErr("");
    if (!isLogin && form.password.length < 8) return setErr(t("reset_min"));
    if (!isLogin && !(consents.terms_privacy && consents.health_data)) return setConsentErr(t("consent_error"));
    setBusy(true);
    try {
      let user;
      if (isLogin) {
        user = await login(form.email, form.password);
      } else {
        user = await register({ ...form, invite_code: tenant ? undefined : form.invite_code, ...consents, tenant_slug: tenant?.slug });
      }
      toast.success(isLogin ? "Bentornato in RISE your LIFE!" : "Account RISE your LIFE creato!");
      if (user.role === "admin") navigate("/admin");
      else if (!user.onboarded) navigate("/onboarding");
      else navigate("/dashboard");
    } catch (e2) {
      const st = e2.response?.status;
      const dt = String(e2.response?.data?.detail || "");
      if (st === 429) setErr(t("invite_code_limit"));
      else if (dt.startsWith("Codice non valido")) setErr(t("invite_code_invalid"));
      else setErr(apiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="App min-h-screen">
      <Nav />
      <div className="max-w-md mx-auto px-6 py-16">
        {tenant && (
          <div className="flex flex-col items-start gap-3 mb-6" data-testid="tenant-onboarding-brand">
            {tenant.logo_url ? (
              <img src={tenant.logo_url} alt={tenant.name} className="h-11 object-contain" data-testid="tenant-logo" />
            ) : (
              <RiseLogo width={54} height={36} />
            )}
            <span className="inline-flex items-center gap-2 text-xs font-medium text-white rounded-full px-3 py-1" style={{ backgroundColor: accent }}>
              {t("tenant_powered_by")}
            </span>
          </div>
        )}
        <h1 className="font-serif text-4xl tracking-tight text-[#2B4A59] mb-2">
          {isLogin ? t("login") : tenant ? `${t("register")} · ${tenant.name}` : t("register")}
        </h1>
        <p className="text-[#6E6B65] mb-8">{tenant ? (tenant.welcome_message || t("tenant_join_sub")) : t("tagline")}</p>
        <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
          {!isLogin && (
            <>
              <div>
                <Label htmlFor="register-first-name">{t("first_name")}</Label>
                <Input id="register-first-name" name="given-name" autoComplete="given-name" data-testid="input-first-name" required value={form.first_name} onChange={set("first_name")} className="rounded-lg h-12 text-base md:text-base mt-1" />
              </div>
              <div>
                <Label htmlFor="register-last-name">{t("last_name")}</Label>
                <Input id="register-last-name" name="family-name" autoComplete="family-name" data-testid="input-last-name" required value={form.last_name} onChange={set("last_name")} className="rounded-lg h-12 text-base md:text-base mt-1" />
              </div>
              {!tenant && (
                <div>
                  <Label htmlFor="register-invite-code">{t("invite_code")}</Label>
                  <Input id="register-invite-code" name="invite-code" autoComplete="off" autoCapitalize="characters" data-testid="input-invite-code" value={form.invite_code} onChange={set("invite_code")} className="rounded-lg h-12 text-base md:text-base mt-1" />
                  <p className="text-[14px] text-[#6E6B65] mt-2" data-testid="invite-code-hint">{t("invite_code_hint")}</p>
                </div>
              )}
            </>
          )}
          <div>
            <Label htmlFor={`${mode}-email`}>{t("email")}</Label>
            <Input id={`${mode}-email`} name="email" autoComplete="email" data-testid="input-email" type="email" required value={form.email} onChange={set("email")} className="rounded-lg h-12 text-base md:text-base mt-1" />
          </div>
          <div>
            <Label htmlFor={`${mode}-password`}>{t("password")}</Label>
            <PasswordInput id={`${mode}-password`} name="password" autoComplete={isLogin ? "current-password" : "new-password"} data-testid="input-password" required minLength={isLogin ? undefined : 8} value={form.password} onChange={set("password")} />
            {!isLogin && <p className="text-[14px] text-[#6E6B65] mt-2" data-testid="password-hint">{t("password_hint")}</p>}
            {isLogin && (
              <Link to="/forgot-password" className="inline-block text-xs text-[#3F6E85] hover:underline mt-2" data-testid="auth-forgot-link">
                {t("forgot_password_q")}
              </Link>
            )}
          </div>
          {!isLogin && <ConsentBoxes value={consents} onChange={(v) => { setConsents(v); setConsentErr(""); }} error={consentErr} />}
          {err && <p role="alert" className="text-sm text-[#B7410E]" data-testid="auth-error">{err}</p>}
          <Button data-testid="auth-submit" type="submit" disabled={busy} className={`w-full rounded-full h-12 text-white ${tenant ? "hover:opacity-90" : "bg-[#3F6E85] hover:bg-[#2B4A59]"}`}
            style={tenant ? { backgroundColor: accent } : undefined}>
            {busy ? "..." : isLogin ? t("login") : t("register")}
          </Button>
        </form>
        <p className="text-sm text-[#6E6B65] mt-6">
          {isLogin ? t("no_account") : t("have_account")}{" "}
          <Link to={isLogin ? "/register" : "/login"} className="text-[#B5651D] font-semibold" data-testid="auth-switch">
            {isLogin ? t("register") : t("login")}
          </Link>
        </p>
        <p className="text-sm text-[#6E6B65] mt-8 pt-6 border-t border-stone-200">
          <FooterLinks className="" separator={" · "} linkClassName="underline hover:text-[#3F6E85]" />
        </p>
        <LegalInfo />
      </div>
    </div>
  );
}
